<section class="card card-padding">
  <div class="flex justify-between items-start mb-4">
    <div class="flex items-start gap-2">
      <button
        type="button"
        (click)="collapsedChange.emit(!collapsed)"
        class="rounded-lg p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-secondary/40 transition-colors mt-0.5 flex-shrink-0"
        [attr.aria-expanded]="!collapsed"
        [attr.aria-label]="
          collapsed ? 'Expand Systematic Review Protocol' : 'Collapse Systematic Review Protocol'
        "
      >
        <svg
          class="w-5 h-5 transition-transform duration-200"
          [class.rotate-180]="!collapsed"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>
      <div>
        <div class="flex items-center gap-1.5">
          <h2 class="section-title">Systematic Review Protocol</h2>
          <app-info-icon
            heading="Systematic Review Protocol"
            content="Documents your planned methodology: research questions, search strategy, and agreement threshold. Serves as a blueprint and audit trail for your review."
            guideAnchor="protocol"
          ></app-info-icon>
        </div>
        <p class="text-sm text-gray-600 mt-1">
          Define the protocol detailing the study setup, research questions, search strategy, and
          kappa threshold.
        </p>
      </div>
    </div>
    @if (!isEditing && isEditable && !saving && !collapsed) {
      <button type="button" (click)="startEditing()" class="btn-primary flex-shrink-0">
        Edit Protocol
      </button>
    }
  </div>

  @if (!collapsed) {
    @if (isEditing) {
      <!-- Edit mode -->
      <form (ngSubmit)="onSubmit()" class="space-y-4">
        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-bold text-slate-700 mb-1">Research Questions</label>
              <textarea
                [(ngModel)]="editResearchQuestions"
                name="researchQuestions"
                rows="4"
                class="input-field w-full leading-relaxed"
                placeholder="e.g. RQ1: What artificial intelligence models are currently used for diagnostic imaging? RQ2: How do deep learning methods compare to traditional classifiers?"
                required
              ></textarea>
            </div>

            <div>
              <label class="block text-sm font-bold text-slate-700 mb-1"
                >Cohen's Kappa Threshold</label
              >
              <p class="text-xs text-slate-500 mb-1">
                Specify target inter-annotator agreement threshold for screening.
              </p>
              <input
                #kappaInput="ngModel"
                type="number"
                [(ngModel)]="editCohenKappaThreshold"
                name="cohenKappaThreshold"
                min="0"
                max="1"
                step="0.05"
                inputmode="decimal"
                placeholder="e.g. 0.60"
                class="input-field w-full md:w-1/2"
                [class.border-red-400]="kappaInput.invalid && kappaInput.touched"
                required
              />
              @if (kappaInput.touched && kappaInput.errors) {
                <p class="mt-1 text-xs text-red-600">
                  @if (kappaInput.errors['required']) {
                    Must be a number.
                  } @else if (kappaInput.errors['min'] || kappaInput.errors['max']) {
                    Must be a number between 0 and 1.
                  } @else {
                    Enter a valid number between 0 and 1 (e.g. 0.60).
                  }
                </p>
              }
            </div>
          </div>

          <div class="space-y-4">
            <div>
              <label class="block text-sm font-bold text-slate-700 mb-1"
                >Search Strategy &amp; Methodology</label
              >
              <textarea
                [(ngModel)]="editSearchMethodology"
                name="searchMethodology"
                rows="4"
                class="input-field w-full leading-relaxed"
                placeholder="e.g. We searched Scopus, PubMed, and IEEE Xplore for diagnostic imaging AI algorithms between 2020 and 2026..."
                required
              ></textarea>
            </div>
          </div>
        </div>

        <div class="flex flex-wrap gap-3 pt-3 border-t border-slate-100 justify-end">
          <button
            type="button"
            (click)="cancelEditing()"
            class="btn btn-secondary px-5 py-2 text-sm font-semibold rounded-lg shadow-sm"
            [disabled]="saving"
          >
            Cancel
          </button>
          <button
            type="submit"
            class="btn btn-primary px-5 py-2 text-sm font-semibold rounded-lg shadow-sm"
            [disabled]="saving || kappaInput.invalid"
          >
            {{ saving ? 'Saving...' : 'Save Changes' }}
          </button>
        </div>
      </form>
    } @else {
      <!-- Read-only view -->
      <div class="space-y-4">
        @if (isLocked) {
          <div class="rounded-xl border border-emerald-200 bg-emerald-50/50 p-4">
            <div class="flex gap-3">
              <div class="text-emerald-700 font-bold">✓</div>
              <div>
                <h3 class="text-sm font-bold text-emerald-800">Protocol Finalized &amp; Locked</h3>
                <p class="text-xs text-emerald-700 mt-0.5 leading-relaxed">
                  This protocol was finalized and locked when the screening phase started. To ensure
                  research integrity, the protocol settings can no longer be altered.
                </p>
              </div>
            </div>
          </div>
        }

        <div class="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div class="space-y-4">
            <div>
              <h3 class="text-sm font-bold text-slate-800 mb-1">Research Questions</h3>
              <div
                class="p-3 bg-slate-50 border border-slate-100 rounded-xl text-sm text-slate-700 whitespace-pre-wrap leading-relaxed"
              >
                {{ protocol?.researchQuestions || 'None defined.' }}
              </div>
            </div>

            <div>
              <h3 class="text-sm font-bold text-slate-800 mb-1">
                Cohen's Kappa Agreement Threshold
              </h3>
              <div
                class="p-3 bg-slate-50 border border-slate-100 rounded-xl text-sm font-mono font-bold text-slate-700 inline-block min-w-[80px] text-center"
              >
                {{ protocol?.cohenKappaThreshold ?? 0.8 | number: '1.2-3' }}
              </div>
            </div>
          </div>

          <div class="space-y-4">
            <div>
              <h3 class="text-sm font-bold text-slate-800 mb-1">
                Search Strategy &amp; Methodology
              </h3>
              <div
                class="p-3 bg-slate-50 border border-slate-100 rounded-xl text-sm text-slate-700 whitespace-pre-wrap leading-relaxed"
              >
                {{ protocol?.searchMethodology || 'None defined.' }}
              </div>
            </div>
          </div>
        </div>

        @if (!isEditable && !isLocked) {
          <p class="text-xs text-amber-600 font-semibold flex items-center gap-1">
            Only the project PI can edit the protocol.
          </p>
        }
      </div>
    }
  }
</section>
